import type { RepositorySnapshotReader } from "../intake/repositorySweepService";
import { createId } from "../../../app/runtimeContext";
import type { SupabaseClient } from "@supabase/supabase-js";

import { CyanGoalPlanningService } from "../autonomy/goalPlanningService";

import { CyanGoalCapabilityService } from "../autonomy/goalCapabilityService";

import { RepositorySweepService } from "../intake/repositorySweepService";

import { ProjectDiagnosticService } from "../diagnostics/projectDiagnosticService";

import { RepairPlanningService } from "../repair/repairPlanningService";

import { CyanOperationalTakeoverService } from "./cyanOperationalTakeoverService";
import { createCyanOwnerAuthorityService } from "../authority/createCyanOwnerAuthorityService";

import { CyanProgramService } from "./cyanProgramService";

import { ExecutionProgramAugmentationService } from "./executionProgramAugmentationService";

import { CyanOperationalSessionService } from "./cyanOperationalSessionService";

import { CyanTakeoverChatController } from "./cyanTakeoverChatController";

import { CyanOwnerDecisionWorkFactory } from "./cyanOwnerDecisionWorkFactory";

import { CyanLiveTakeoverBridge } from "./cyanLiveTakeoverBridge";

import { SupabaseCyanOperationalSessionStore } from "./supabaseCyanOperationalSessionStore";

import { SupabaseExecutionProgramRepository } from "./supabaseExecutionProgramRepository";

import type { CapabilityAvailabilityReader } from "../autonomy/workerSelectionService";

import type { MultiAgentScheduler } from "./multiAgentScheduler";

import {
  InMemoryMissionLedger,
  MissionControl,
  MissionProgramAuthority,
} from "../../missionControl";

const cyanGoalIdFactory: ConstructorParameters<
  typeof CyanGoalPlanningService
>[0] = {
  createGoalId: () => createId("cyan_goal"),
};

export interface BrowserTakeoverBridgeDependencies {
  readonly supabase: SupabaseClient;

  readonly repositoryReader: RepositorySnapshotReader;

  readonly capabilityAvailability: CapabilityAvailabilityReader;

  readonly scheduler: MultiAgentScheduler;
}

/**
 * Canonical browser orchestration assembly.
 *
 * This assembles Cyan's control plane only.
 * Agents/workers remain the execution authority.
 */
export function createBrowserTakeoverBridge(
  dependencies: BrowserTakeoverBridgeDependencies,
) {
  const programs = new SupabaseExecutionProgramRepository(
    dependencies.supabase,
  );

  const programService = new CyanProgramService(programs);

  const missionControl = new MissionControl({
    ledger: new InMemoryMissionLedger(),

    /*
     * Program authorization is the control-plane responsibility here.
     * Specialist execution remains below the durable program boundary.
     */
    orchestrators: new Map(),
  });

  const missionAuthority = new MissionProgramAuthority(missionControl);

  const ownerAuthority = createCyanOwnerAuthorityService(dependencies.supabase);

  const takeover = new CyanOperationalTakeoverService(
    new CyanGoalPlanningService(cyanGoalIdFactory),
    new CyanGoalCapabilityService(dependencies.capabilityAvailability),
    new RepositorySweepService(dependencies.repositoryReader),
    new ProjectDiagnosticService(),
    new RepairPlanningService(),
    programService,
    dependencies.scheduler,
    missionAuthority,
    ownerAuthority,
  );

  const sessions = new CyanOperationalSessionService(
    takeover,
    programs,
    new ExecutionProgramAugmentationService(programs),
  );

  const controller = new CyanTakeoverChatController(
    sessions,
    new CyanOwnerDecisionWorkFactory(),
  );

  return new CyanLiveTakeoverBridge(
    controller,
    new SupabaseCyanOperationalSessionStore(dependencies.supabase),
  );
}
